import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend,
  LineChart, Line, Area, AreaChart,
  RadialBarChart, RadialBar
} from 'recharts';
import { Users, Building2, TrendingUp, Calendar, ArrowUp, ArrowDown } from 'lucide-react';

// Sample data for mockup
const monthlyActiveUsers = [
  { month: 'Jul', users: 142, newUsers: 28 },
  { month: 'Aug', users: 168, newUsers: 35 },
  { month: 'Sep', users: 185, newUsers: 22 },
  { month: 'Oct', users: 201, newUsers: 31 },
  { month: 'Nov', users: 224, newUsers: 38 },
  { month: 'Dec', users: 247, newUsers: 45 },
];

const divisionData = [
  { name: 'Commercial', active: 85, total: 92 },
  { name: 'Residential', active: 64, total: 71 },
  { name: 'Industrial', active: 48, total: 55 },
  { name: 'Service', active: 32, total: 38 },
  { name: 'New Construction', active: 18, total: 22 },
];

const userActivityBreakdown = [
  { name: 'Daily Active', value: 156, color: '#22c55e' },
  { name: 'Weekly Active', value: 67, color: '#3b82f6' },
  { name: 'Monthly Active', value: 24, color: '#f59e0b' },
];

const companyBillingData = [
  { company: 'ABC Electric', activeUsers: 45, totalUsers: 52, revenue: 2250, trend: 'up' },
  { company: 'XYZ Plumbing', activeUsers: 38, totalUsers: 41, revenue: 1900, trend: 'up' },
  { company: 'Metro HVAC', activeUsers: 28, totalUsers: 35, revenue: 1400, trend: 'down' },
  { company: 'City Builders', activeUsers: 62, totalUsers: 68, revenue: 3100, trend: 'up' },
  { company: 'Prime Mechanical', activeUsers: 21, totalUsers: 24, revenue: 1050, trend: 'same' },
];

const COLORS = ['#22c55e', '#3b82f6', '#f59e0b', '#ef4444', '#8b5cf6'];

const AnalyticsMockup = () => {
  const [viewType, setViewType] = useState('admin'); // admin, division, superadmin
  const [selectedMonth, setSelectedMonth] = useState('December 2025');

  // Circular progress component
  const CircularProgress = ({ value, max, label, color = '#22c55e', size = 120 }) => {
    const percentage = Math.round((value / max) * 100);
    const data = [{ value: percentage, fill: color }, { value: 100 - percentage, fill: '#e5e7eb' }];
    
    return (
      <div className="relative" style={{ width: size, height: size }}>
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={data}
              cx="50%"
              cy="50%"
              innerRadius={size * 0.35}
              outerRadius={size * 0.45}
              startAngle={90}
              endAngle={-270}
              dataKey="value"
              stroke="none"
            >
              {data.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.fill} />
              ))}
            </Pie>
          </PieChart>
        </ResponsiveContainer>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className="text-2xl font-bold">{percentage}%</span>
          <span className="text-xs text-gray-500">{label}</span>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-100 p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Analytics Dashboard</h1>
            <p className="text-gray-600">User activity and billing insights</p>
          </div>
          <div className="flex gap-3">
            <Select value={viewType} onValueChange={setViewType}>
              <SelectTrigger className="w-40">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="admin">Admin View</SelectItem>
                <SelectItem value="division">Division View</SelectItem>
                <SelectItem value="superadmin">SuperAdmin View</SelectItem>
              </SelectContent>
            </Select>
            <Select value={selectedMonth} onValueChange={setSelectedMonth}>
              <SelectTrigger className="w-44">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="December 2025">December 2025</SelectItem>
                <SelectItem value="November 2025">November 2025</SelectItem>
                <SelectItem value="October 2025">October 2025</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* View Type Label */}
        <div className="flex items-center gap-2">
          <Badge className={`text-sm px-3 py-1 ${
            viewType === 'admin' ? 'bg-blue-600' : 
            viewType === 'division' ? 'bg-purple-600' : 'bg-green-600'
          }`}>
            {viewType === 'admin' ? '🏢 Company Admin View' : 
             viewType === 'division' ? '📁 Division View' : '👑 SuperAdmin View'}
          </Badge>
        </div>

        {/* Key Metrics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white">
            <CardContent className="p-6">
              <div className="flex justify-between items-start">
                <div>
                  <p className="text-blue-100 text-sm">Active Users</p>
                  <p className="text-4xl font-bold mt-1">247</p>
                  <p className="text-blue-200 text-sm mt-2 flex items-center">
                    <ArrowUp className="w-4 h-4 mr-1" /> +23 from last month
                  </p>
                </div>
                <Users className="w-10 h-10 text-blue-200" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-green-500 to-green-600 text-white">
            <CardContent className="p-6">
              <div className="flex justify-between items-start">
                <div>
                  <p className="text-green-100 text-sm">Total Users</p>
                  <p className="text-4xl font-bold mt-1">278</p>
                  <p className="text-green-200 text-sm mt-2">89% active rate</p>
                </div>
                <Building2 className="w-10 h-10 text-green-200" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-purple-500 to-purple-600 text-white">
            <CardContent className="p-6">
              <div className="flex justify-between items-start">
                <div>
                  <p className="text-purple-100 text-sm">New This Month</p>
                  <p className="text-4xl font-bold mt-1">45</p>
                  <p className="text-purple-200 text-sm mt-2 flex items-center">
                    <ArrowUp className="w-4 h-4 mr-1" /> +18% growth
                  </p>
                </div>
                <TrendingUp className="w-10 h-10 text-purple-200" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-orange-500 to-orange-600 text-white">
            <CardContent className="p-6">
              <div className="flex justify-between items-start">
                <div>
                  <p className="text-orange-100 text-sm">{viewType === 'superadmin' ? 'Monthly Revenue' : 'Divisions'}</p>
                  <p className="text-4xl font-bold mt-1">{viewType === 'superadmin' ? '$9,700' : '5'}</p>
                  <p className="text-orange-200 text-sm mt-2">
                    {viewType === 'superadmin' ? '247 billable users' : 'All divisions active'}
                  </p>
                </div>
                <Calendar className="w-10 h-10 text-orange-200" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Charts Row */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Monthly Active Users Trend */}
          <Card className="lg:col-span-2">
            <CardHeader>
              <CardTitle>Monthly Active Users Trend</CardTitle>
              <CardDescription>Users who logged in at least once during the month</CardDescription>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <AreaChart data={monthlyActiveUsers}>
                  <defs>
                    <linearGradient id="colorUsers" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="#3b82f6" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis dataKey="month" stroke="#6b7280" />
                  <YAxis stroke="#6b7280" />
                  <Tooltip 
                    contentStyle={{ backgroundColor: '#fff', borderRadius: '8px', border: '1px solid #e5e7eb' }}
                  />
                  <Area 
                    type="monotone" 
                    dataKey="users" 
                    stroke="#3b82f6" 
                    strokeWidth={2}
                    fillOpacity={1} 
                    fill="url(#colorUsers)" 
                    name="Active Users"
                  />
                  <Line 
                    type="monotone" 
                    dataKey="newUsers" 
                    stroke="#22c55e" 
                    strokeWidth={2}
                    dot={{ fill: '#22c55e', strokeWidth: 2 }}
                    name="New Users"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          {/* User Activity Breakdown */}
          <Card>
            <CardHeader>
              <CardTitle>Activity Breakdown</CardTitle>
              <CardDescription>This month's user engagement</CardDescription>
            </CardHeader>
            <CardContent className="flex flex-col items-center">
              <ResponsiveContainer width="100%" height={200}>
                <PieChart>
                  <Pie
                    data={userActivityBreakdown}
                    cx="50%"
                    cy="50%"
                    innerRadius={50}
                    outerRadius={80}
                    paddingAngle={5}
                    dataKey="value"
                  >
                    {userActivityBreakdown.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
              <div className="flex flex-wrap justify-center gap-4 mt-4">
                {userActivityBreakdown.map((item) => (
                  <div key={item.name} className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full" style={{ backgroundColor: item.color }} />
                    <span className="text-sm text-gray-600">{item.name}: {item.value}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Division/Company Breakdown */}
        {viewType !== 'superadmin' ? (
          <Card>
            <CardHeader>
              <CardTitle>Active Users by Division</CardTitle>
              <CardDescription>Breakdown of active users across divisions</CardDescription>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={divisionData} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis type="number" stroke="#6b7280" />
                  <YAxis dataKey="name" type="category" width={120} stroke="#6b7280" />
                  <Tooltip 
                    contentStyle={{ backgroundColor: '#fff', borderRadius: '8px', border: '1px solid #e5e7eb' }}
                  />
                  <Bar dataKey="active" fill="#3b82f6" name="Active Users" radius={[0, 4, 4, 0]} />
                  <Bar dataKey="total" fill="#e5e7eb" name="Total Users" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        ) : (
          /* SuperAdmin Company Billing Table */
          <Card>
            <CardHeader>
              <CardTitle>Company Billing Overview</CardTitle>
              <CardDescription>Active users and billing by company for {selectedMonth}</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-3 px-4 font-semibold text-gray-700">Company</th>
                      <th className="text-center py-3 px-4 font-semibold text-gray-700">Active Users</th>
                      <th className="text-center py-3 px-4 font-semibold text-gray-700">Total Users</th>
                      <th className="text-center py-3 px-4 font-semibold text-gray-700">Activity Rate</th>
                      <th className="text-right py-3 px-4 font-semibold text-gray-700">Billable Amount</th>
                      <th className="text-center py-3 px-4 font-semibold text-gray-700">Trend</th>
                    </tr>
                  </thead>
                  <tbody>
                    {companyBillingData.map((company, index) => {
                      const activityRate = Math.round((company.activeUsers / company.totalUsers) * 100);
                      return (
                        <tr key={index} className="border-b hover:bg-gray-50">
                          <td className="py-4 px-4">
                            <div className="font-medium text-gray-900">{company.company}</div>
                          </td>
                          <td className="py-4 px-4 text-center">
                            <span className="text-2xl font-bold text-blue-600">{company.activeUsers}</span>
                          </td>
                          <td className="py-4 px-4 text-center text-gray-600">{company.totalUsers}</td>
                          <td className="py-4 px-4">
                            <div className="flex items-center justify-center gap-2">
                              <div className="w-24 h-2 bg-gray-200 rounded-full overflow-hidden">
                                <div 
                                  className="h-full bg-green-500 rounded-full" 
                                  style={{ width: `${activityRate}%` }}
                                />
                              </div>
                              <span className="text-sm text-gray-600">{activityRate}%</span>
                            </div>
                          </td>
                          <td className="py-4 px-4 text-right">
                            <span className="text-lg font-semibold text-green-600">${company.revenue.toLocaleString()}</span>
                          </td>
                          <td className="py-4 px-4 text-center">
                            {company.trend === 'up' ? (
                              <Badge className="bg-green-100 text-green-700">
                                <ArrowUp className="w-3 h-3 mr-1" /> Up
                              </Badge>
                            ) : company.trend === 'down' ? (
                              <Badge className="bg-red-100 text-red-700">
                                <ArrowDown className="w-3 h-3 mr-1" /> Down
                              </Badge>
                            ) : (
                              <Badge className="bg-gray-100 text-gray-700">Stable</Badge>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr className="bg-gray-50 font-semibold">
                      <td className="py-4 px-4">Total</td>
                      <td className="py-4 px-4 text-center text-blue-600">
                        {companyBillingData.reduce((sum, c) => sum + c.activeUsers, 0)}
                      </td>
                      <td className="py-4 px-4 text-center">
                        {companyBillingData.reduce((sum, c) => sum + c.totalUsers, 0)}
                      </td>
                      <td className="py-4 px-4"></td>
                      <td className="py-4 px-4 text-right text-green-600">
                        ${companyBillingData.reduce((sum, c) => sum + c.revenue, 0).toLocaleString()}
                      </td>
                      <td></td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Circular Progress Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="p-6 flex flex-col items-center">
              <CircularProgress value={247} max={278} label="Active Rate" color="#22c55e" />
              <p className="mt-3 font-semibold text-gray-700">User Activity</p>
              <p className="text-sm text-gray-500">247 of 278 users active</p>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-6 flex flex-col items-center">
              <CircularProgress value={45} max={50} label="Completion" color="#3b82f6" />
              <p className="mt-3 font-semibold text-gray-700">Training Done</p>
              <p className="text-sm text-gray-500">90% completed training</p>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6 flex flex-col items-center">
              <CircularProgress value={5} max={5} label="Divisions" color="#8b5cf6" />
              <p className="mt-3 font-semibold text-gray-700">Division Activity</p>
              <p className="text-sm text-gray-500">All 5 divisions active</p>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6 flex flex-col items-center">
              <CircularProgress value={31} max={31} label="Days Left" color="#f59e0b" />
              <p className="mt-3 font-semibold text-gray-700">Billing Cycle</p>
              <p className="text-sm text-gray-500">Month ends Dec 31</p>
            </CardContent>
          </Card>
        </div>

      </div>
    </div>
  );
};

export default AnalyticsMockup;
